import React, { useState, useEffect } from 'react';
import {
  searchCustomers, uploadPaymentProof, submitPaymentReceived,
  getUnclaimedBankCredits, assignTransactionToCustomer,
} from '../services/api';
import { showSuccess, showError } from './ToastNotifications';
import { clampDecimals, sanitizeAmountInput, amountInputGuard, DOCUMENT_COMPRESSION_OPTIONS, compressImage, formatCurrency } from '../utils/helpers';
import ImageUploadBox from './ImageUploadBox';
import MatchingCreditPrompt from './MatchingCreditPrompt';
import './CreatePRModal.css';

// Sales-initiated "Payment Received": upload the receipt(s) the CUSTOMER handed
// them (their own transfer screenshot, a cash slip, etc.) + a note for
// finance. The customer link is COMPULSORY (operator rules 2026-08-01 and
// 2026-08-05 — the old "Skip, payer not identified" path is gone; the server
// rejects customer-less submissions). Modeled closely on RequestPaymentModal
// (same shell/search/proof conventions) so the two "sales talks to finance
// about money" flows read the same.
//
// MULTI-RECEIPT (operator, 2026-08-03): "1 receipt = 1 PR" — a customer who
// pays in several transfers hands over several slips, and finance records one
// credit per slip against this one submission. The receipt slot is therefore a
// LIST, mirroring the P2P Sell order form's `sellReceipts` (PlatformManagement.js)
// and its per-slot render (CreatePlatformOrderModal.js) — same idioms, same
// wording style, just wired to this flow's own upload endpoint.
function SubmitPaymentReceivedModal({ isOpen, onClose, onSuccess, user }) {
  // HOLIDAY COVER (operator 2026-09-04): every customer is searchable; own vs.
  // a colleague's is badged, and choosing a colleague's customer confirms.
  const isMyCustomer = (c) => !c || user?.role !== 'sales' || Number(c.assigned_to) === Number(user?.id);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchedQuery, setSearchedQuery] = useState('');
  const [customer, setCustomer] = useState(null);
  const [note, setNote] = useState('');
  // One slot per receipt. `url` is the already-uploaded image (upload-first,
  // same as the P2P Sell slots), `amount` is what THAT slip is worth (now
  // compulsory per row — see the top-level Amount field below), `skipAmount`
  // and `matches` are this row's own duplicate-PR fail-safe state (see below).
  const emptyReceipt = () => ({ url: '', amount: '', skipAmount: null, matches: [] });
  const [receipts, setReceipts] = useState([emptyReceipt()]);
  const [uploading, setUploading] = useState(false); // overlay-paste upload in flight
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  // DUPLICATE-PR FAIL-SAFE, now PER ROW. Finance may already have recorded the
  // exact credit one of these slips represents straight off the bank
  // statement, in which case submitting it asks them to record the same money
  // a second time. Each row keeps its own `matches` (unclaimed credits for
  // THAT row's amount) and `skipAmount` (the amount the operator already said
  // "no, different payment" for, so the prompt isn't repeated for it) — a
  // per-row concern, because two different slips can each coincidentally match
  // a different unclaimed credit. `promptRow` is the index of the row
  // currently showing the prompt (only one prompt can be open at a time; the
  // shared `matches` list a single amount used to carry now lives on the row).
  const [promptRow, setPromptRow] = useState(null);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setQuery(''); setResults([]); setCustomer(null);
      setNote('');
      setReceipts([emptyReceipt()]);
      setErrors({});
      setPromptRow(null); setLinking(false); setUploading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Rounds the same way the server does (`Math.round(sum * 100) / 100`) so the
  // cross-check total we send matches the server's own sum of the stored rows
  // to the cent, never tripping the "stated total does not match" 400 on our
  // own honestly-computed total.
  const receiptsTotal = (list) =>
    Math.round(list.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0) * 100) / 100;

  const setReceiptAt = (i, patch) =>
    setReceipts((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addReceiptSlot = () => setReceipts((prev) => [...prev, emptyReceipt()]);
  const removeReceiptSlot = (i) =>
    setReceipts((prev) => (prev.length === 1 ? [emptyReceipt()] : prev.filter((_, idx) => idx !== i)));

  // Look for an already-recorded credit of exactly this row's amount. Runs on
  // that row's amount blur — early, BEFORE the row is submitted — so the
  // operator finds out there is nothing to submit before doing the work, and
  // again as a per-row backstop at submit.
  const findMatchesForRow = async (i, amt) => {
    const value = parseFloat(amt);
    if (!Number.isFinite(value) || value <= 0) { setReceiptAt(i, { matches: [] }); return []; }
    try {
      const rows = await getUnclaimedBankCredits(value);
      const list = Array.isArray(rows) ? rows : [];
      setReceiptAt(i, { matches: list });
      return list;
    } catch (e) {
      // A matcher failure must never block a legitimate submission — the worst
      // case is the old behaviour (finance sees a duplicate and cancels it).
      setReceiptAt(i, { matches: [] });
      return [];
    }
  };

  const handleRowAmountBlur = async (i) => {
    if (submitting || linking) return;
    const row = receipts[i];
    if (!row) return;
    const value = parseFloat(row.amount);
    if (!Number.isFinite(value) || value <= 0) return;
    if (row.skipAmount != null && Math.abs(row.skipAmount - value) < 0.005) return;
    const list = await findMatchesForRow(i, value);
    if (list.length > 0) setPromptRow(i);
  };

  // YES — this row's money IS what finance already recorded. Attribute that
  // credit to the customer sales picked, through the SAME assign-to-customer
  // path a late attribution uses (customer ledger + bank row in one
  // server-side booking, NAV-neutral). No payment_received task is created
  // for this slip, so finance is never asked to record it again — which means
  // this row must NOT be part of the submission any more: DROP it from the
  // list. The row's already-uploaded image goes WITH the link as the
  // customer's receipt (bank_credit_link_receipts, 2026-10-29), so it is no
  // longer orphaned.
  const handleLinkMatch = async (credit) => {
    if (promptRow == null) return;
    if (!customer) {
      setPromptRow(null);
      setErrors((prev) => ({ ...prev, customer: 'Select the customer who paid us first' }));
      return;
    }
    const rowIndex = promptRow;
    const wasOnlyRow = receipts.length === 1;
    // The slip's receipt travels with the link (operator rule 2026-10-29:
    // linking a finance-recorded credit needs the customer's receipt, stored
    // like a PR request's) — so it must be uploaded before YES.
    const rowUrl = receipts[rowIndex]?.url;
    if (!rowUrl) {
      setPromptRow(null);
      setErrors((prev) => ({ ...prev, [`receipt_${rowIndex}`]: "Upload this slip's receipt first — it is saved with the payment when you link it" }));
      return;
    }
    setLinking(true);
    try {
      // Not your customer → the cover confirmation was given when you picked
      // them; the server alerts their PIC (same as submitting the receipt).
      await assignTransactionToCustomer(credit.id, customer.id, { onBehalf: !isMyCustomer(customer), receiptUrls: [rowUrl] });
      if (wasOnlyRow) {
        // Nothing left to submit — identical to today's single-receipt behaviour.
        showSuccess(`${formatCurrency(credit.amount, 'MYR')} linked to ${customer.customer_name} — finance had already recorded it, so nothing was sent to them.`);
        setPromptRow(null);
        if (onSuccess) onSuccess();
        setTimeout(() => onClose(), 400);
        return;
      }
      showSuccess(`${formatCurrency(credit.amount, 'MYR')} linked to ${customer.customer_name} — finance had already recorded it, so it was removed from this submission.`);
      setPromptRow(null);
      setReceipts((prev) => prev.filter((_, idx) => idx !== rowIndex));
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to link that payment';
      showError(msg);
      // Refresh this row's matches: a 409 means someone claimed it while we were deciding.
      const row = receipts[rowIndex];
      const left = await findMatchesForRow(rowIndex, row?.amount);
      if (left.length === 0) setPromptRow(null);
    } finally {
      setLinking(false);
    }
  };

  // NO — a different payment that happens to be the same amount. Carry on
  // exactly as before and stop asking about this row's figure — latched PER
  // ROW (not globally) since another row can legitimately share the amount
  // the operator just said "no" to for this one.
  const handleSkipMatch = () => {
    if (promptRow == null) return;
    const i = promptRow;
    setPromptRow(null);
    const value = parseFloat(receipts[i]?.amount);
    if (Number.isFinite(value)) setReceiptAt(i, { skipAmount: value });
  };

  // Type-ahead customer search (min 2 chars, lightly debounced) — same as RequestPaymentModal.
  useEffect(() => {
    if (!isOpen || customer) return;
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    let active = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const data = await searchCustomers(q, { scope: 'all' });
        if (active) { setResults(Array.isArray(data) ? data : []); setSearchedQuery(q); }
      } catch (e) {
        if (active) { setResults([]); setSearchedQuery(q); }
      } finally {
        if (active) setSearching(false);
      }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [query, isOpen, customer]);

  const selectCustomer = (c) => {
    if (!isMyCustomer(c)) {
      const pic = c.assigned_to_name || 'another sales user';
      if (!window.confirm(`${c.customer_name} is ${pic}'s customer.\n\nYou are reporting a payment received on their behalf (e.g. holiday cover); ${pic} will be notified.\n\nContinue with this customer?`)) return;
    }
    setCustomer(c);
    setResults([]);
    setQuery('');
  };

  const clearCustomer = () => setCustomer(null);

  // Paste a screenshot anywhere in the modal — fills the first slot with no
  // image yet, appending a new slot when they are all taken (same convention
  // as `uploadSellReceiptToNextSlot` in PlatformManagement.js), so pasting
  // several slips in a row just works.
  const uploadToNextSlot = async (file) => {
    if (!file.type || !file.type.startsWith('image/')) { showError('Please paste an image'); return; }
    if (file.size > 10 * 1024 * 1024) { showError('Image must be under 10MB'); return; }
    const target = receipts.findIndex((r) => !r.url);
    const index = target >= 0 ? target : receipts.length;
    if (target < 0) setReceipts((prev) => [...prev, emptyReceipt()]);
    setUploading(true);
    try {
      let out = file;
      try { out = (await compressImage(file, DOCUMENT_COMPRESSION_OPTIONS)).file; }
      catch (e) { console.warn('Receipt compression failed; using original:', e); } // always fall back to the original on failure
      const res = await uploadPaymentProof(out);
      if (res?.url) setReceiptAt(index, { url: res.url });
      else showError('Upload failed — no URL returned');
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to upload the receipt image');
    } finally {
      setUploading(false);
    }
  };

  const handleOverlayPaste = (e) => {
    if (submitting || uploading) return;
    const items = e.clipboardData?.items || [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type && items[i].type.indexOf('image') !== -1) {
        uploadToNextSlot(items[i].getAsFile());
        break;
      }
    }
  };

  const validate = () => {
    const e = {};
    // Note is OPTIONAL (operator rule 2026-07-31); customer is COMPULSORY
    // (operator rule 2026-08-01) — sales knows who paid. Amount is now
    // COMPULSORY PER ROW (operator 2026-08-03: "the sales has to specify how
    // much is the receipt worth") — there is no longer a single top-level
    // amount to validate, only the rows that produce it.
    if (!customer) e.customer = 'Select the customer who paid us';
    receipts.forEach((r, i) => {
      if (!r.url) e[`receipt_${i}`] = 'A receipt image is required';
      const amt = parseFloat(r.amount);
      if (!r.amount || isNaN(amt) || amt <= 0) e[`amount_${i}`] = 'The amount received is required';
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    // BACKSTOP: the blur check can be skipped entirely (paste + Enter), and a
    // matching credit can also be recorded between blur and submit. Re-check
    // every row that hasn't already been answered "no" for its current
    // amount; the first match found opens the prompt for THAT row and aborts
    // the submit, same shape as the single-row version did.
    for (let i = 0; i < receipts.length; i++) {
      const row = receipts[i];
      const amt = parseFloat(row.amount);
      if (row.skipAmount != null && Math.abs(row.skipAmount - amt) < 0.005) continue;
      const list = await findMatchesForRow(i, amt);
      if (list.length > 0) { setPromptRow(i); return; }
    }
    setSubmitting(true);
    try {
      await submitPaymentReceived({
        receipts: receipts.map((r) => ({ receipt_url: r.url, amount: parseFloat(r.amount) })),
        note: note.trim()
          || (customer ? `Payment from ${customer.customer_name}` : 'Payment received — see receipt'),
        ...(customer ? { customer_id: customer.id } : {}),
        amount: receiptsTotal(receipts),
      });
      showSuccess(receipts.length > 1 ? 'Receipts sent to finance' : 'Receipt sent to finance');
      if (onSuccess) onSuccess();
      setTimeout(() => onClose(), 400);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to submit the receipt';
      showError(msg);
      setErrors((prev) => ({ ...prev, submit: msg }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => { if (!submitting) onClose(); };

  if (!isOpen) return null;

  const total = receiptsTotal(receipts);
  const activeMatches = promptRow != null ? (receipts[promptRow]?.matches || []) : [];
  const activeAmount = promptRow != null ? (parseFloat(receipts[promptRow]?.amount) || 0) : 0;

  return (
    // Deliberately NO close-on-overlay-click — mirrors RequestPaymentModal, so
    // a mis-click outside the box doesn't throw away a half-filled submission.
    <div className="modal-overlay" onPaste={handleOverlayPaste}>
      <div className="modal-content create-pr-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>Payment Received</h3>
          <button onClick={handleClose} className="close-button" disabled={submitting}>&times;</button>
        </div>

        <div className="modal-body">
          <p style={{ marginTop: 0, color: '#57606a', fontSize: 14 }}>
            Upload the receipt(s) the customer gave you (their transfer screenshot, a cash slip, etc.) and a short note.
            One receipt per transfer — finance records one payment received (PR) for each of them.
          </p>

          <form onSubmit={handleSubmit}>
            {/* Customer link — COMPULSORY (operator rules 2026-08-01 +
                2026-08-05: "customer selection are compulsory", the old
                Skip — payer-not-identified path is gone). The server enforces
                the same rule, so this form never offers a customer-less
                submission. */}
            <div className="form-group">
              <label className="form-label">Customer <span className="required">*</span></label>
              {customer ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px' }}>
                  <div>
                    <strong>{customer.customer_name}</strong>
                    {customer.customer_nickname ? ` (${customer.customer_nickname})` : ''} — {customer.customer_number}
                  </div>
                  <button type="button" className="btn btn-secondary" onClick={clearCustomer} disabled={submitting} style={{ padding: '2px 10px' }}>Clear</button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name, number or nickname…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  {searching && <small className="form-text">Searching…</small>}
                  {errors.customer && <span className="error-message">{errors.customer}</span>}
                  {!searching && query.trim().length >= 2 && searchedQuery === query.trim() && results.length === 0 && (
                    <small className="form-text" style={{ color: '#b45309' }}>No customer matches “{query.trim()}” — check the spelling, or create the profile in Customer Management first.</small>
                  )}
                  {results.length > 0 && (
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 6, maxHeight: 200, overflowY: 'auto' }}>
                      {results.map((c) => (
                        <div
                          key={c.id}
                          onClick={() => selectCustomer(c)}
                          style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                            ...(user?.role === 'sales' ? (isMyCustomer(c) ? { borderLeft: '4px solid #16a34a' } : { borderLeft: '4px solid #f59e0b', background: '#fffbeb' }) : {}) }}
                        >
                          <strong>{c.customer_name}</strong>
                          {c.customer_nickname ? ` (${c.customer_nickname})` : ''} — {c.customer_number}
                          {user?.role === 'sales' && (
                            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 8,
                              color: isMyCustomer(c) ? '#166534' : '#92400e', background: isMyCustomer(c) ? '#dcfce7' : '#fef3c7' }}>
                              {isMyCustomer(c) ? 'Your customer' : `PIC: ${c.assigned_to_name || 'another sales'}`}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* READ-ONLY computed total. The amount now has exactly ONE
                source of truth — the per-row amounts below — so leaving this
                field writable would create a second writer for the same
                fact, the exact class of bug this codebase is systematically
                removing (one fact = one writer). It stays VISIBLE because
                both sales and finance are used to reading the total off it. */}
            <div className="form-group">
              <label className="form-label">Amount (MYR)</label>
              <input
                type="text"
                className="form-control"
                value={total > 0 ? total.toFixed(2) : ''}
                placeholder="0.00"
                readOnly
                disabled
              />
              <small className="form-text">Total of the receipts below</small>
            </div>

            <div className="form-group">
              <label className="form-label">Note for finance (optional)</label>
              <textarea
                className={`form-control ${errors.note ? 'error' : ''}`}
                rows={3}
                placeholder="e.g. Payment for order #123, paid via Maybank at 3pm"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={submitting}
              />
              {errors.note && <span className="error-message">{errors.note}</span>}
            </div>

            {/* ===== THE CUSTOMER'S RECEIPT(S) — ONE SLOT PER SLIP =====
                "1 receipt = 1 PR" (operator, 2026-08-03): a customer who pays
                in several transfers hands over several slips, and finance
                records one credit per slip against this submission. Mirrors
                the P2P Sell order form's receipt slots (PlatformManagement.js
                / CreatePlatformOrderModal.js) — same list/add/remove/paste
                idioms, wired to this flow's own upload endpoint. */}
            <div className="form-group">
              <label className="form-label">Receipt(s) <span className="required">*</span></label>
              {receipts.map((r, i) => (
                <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <strong style={{ fontSize: 13 }}>Receipt {i + 1}{receipts.length > 1 ? ` of ${receipts.length}` : ''}</strong>
                    {(receipts.length > 1 || r.url) && (
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => removeReceiptSlot(i)}
                        disabled={submitting}
                        title="Remove this receipt"
                      >Remove</button>
                    )}
                  </div>
                  <ImageUploadBox
                    endpoint="/upload/payment-proof"
                    currentUrl={r.url}
                    compressionOptions={DOCUMENT_COMPRESSION_OPTIONS}
                    onUploadSuccess={(url) => setReceiptAt(i, { url })}
                    helperText="Click, drag-drop, or paste (Ctrl/Cmd+V) — pasting anywhere in this window fills the next empty receipt"
                  />
                  {errors[`receipt_${i}`] && <span className="error-message" style={{ display: 'block', marginTop: 4 }}>{errors[`receipt_${i}`]}</span>}

                  <div style={{ marginTop: 8 }}>
                    <label className="form-label" style={{ fontSize: 13 }}>Amount (MYR) <span className="required">*</span></label>
                    <input
                      type="number"
                      className={`form-control ${errors[`amount_${i}`] ? 'error' : ''}`}
                      step="0.01" min="0.01"
                      placeholder="0.00"
                      value={r.amount}
                      onChange={(e) => setReceiptAt(i, { amount: clampDecimals(sanitizeAmountInput(e.target.value), 2) })}
                      {...amountInputGuard}
                      onBlur={() => handleRowAmountBlur(i)}
                      disabled={submitting}
                    />
                    {errors[`amount_${i}`] && <span className="error-message">{errors[`amount_${i}`]}</span>}
                    {/* Standing hint once the operator has said "not the same
                        payment" for THIS row — the match is still there, so
                        make the way back obvious, same as the single-row
                        version did, now scoped to the row it belongs to. */}
                    {promptRow !== i && r.matches.length > 0 && (
                      <small className="form-text" style={{ display: 'block' }}>
                        Finance already recorded {r.matches.length === 1 ? 'a payment' : `${r.matches.length} payments`} of this amount.{' '}
                        <span className="text-link" style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setPromptRow(i)}>
                          Check {r.matches.length === 1 ? 'it' : 'them'} again
                        </span>
                      </small>
                    )}
                  </div>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button type="button" className="btn btn-sm btn-outline" onClick={addReceiptSlot} disabled={submitting}>
                  + Add another receipt
                </button>
                {uploading && <small className="form-text">Uploading pasted screenshot…</small>}
              </div>
            </div>

            {errors.submit && <div className="alert alert-error">{errors.submit}</div>}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={submitting || uploading}>
                {submitting ? 'Sending…' : 'Send to finance'}
              </button>
              <button type="button" onClick={handleClose} className="btn btn-secondary" disabled={submitting}>Cancel</button>
            </div>
          </form>
        </div>
      </div>

      <MatchingCreditPrompt
        user={user}
        isOpen={promptRow != null}
        credits={activeMatches}
        amount={activeAmount}
        busy={linking}
        onLink={handleLinkMatch}
        onSkip={handleSkipMatch}
        onClose={handleSkipMatch}
      />
    </div>
  );
}

export default SubmitPaymentReceivedModal;
